import React, { HTMLProps } from "react";
import styles from "./Select.module.css";

// Reusable dropdown over a native <select> (the app had none — only ad-hoc
// native selects). Styled to match the app's form controls. Values are always
// strings (the OME engine reports every scalar as a string). If the current
// value is not among the options it is still shown (flagged "not found") rather
// than silently dropped — see the rendition ref-picker.

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  options: SelectOption[];
  onValue?: (value: string) => void;
  // Leading entry shown for the empty value; emitting "" means "unset".
  placeholder?: string;
  disabled?: boolean;
  // Render as a warning (e.g. a stale reference the value no longer resolves to).
  invalid?: boolean;
  containerClassName?: string;
}

const Select = ({
  value,
  options,
  onValue,
  placeholder,
  disabled,
  invalid,
  containerClassName,
  ...props
}: SelectProps & Omit<Partial<HTMLProps<HTMLSelectElement>>, "value" | "onChange" | "disabled">) => {
  const current = value != null ? String(value) : "";
  const known = current === "" || options.some((option) => option.value === current);

  const classes = [styles.select];
  if (invalid) classes.push(styles.invalid);
  if (containerClassName) classes.push(containerClassName);

  return (
    <select
      className={classes.join(" ")}
      value={current}
      disabled={disabled}
      onChange={(event) => onValue?.(event.currentTarget.value)}
      {...props}
    >
      <option value="">{placeholder ?? "— default —"}</option>
      {!known ? (
        <option value={current}>{`${current} — not found ⚠`}</option>
      ) : null}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export default Select;
